import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
    ArrowRight,
    EyeSlash,
    ShieldCheck,
    Scales,
    Target,
    SealCheck,
} from "@phosphor-icons/react";
import { trackEvent } from "../lib/api";

const STEPS = [
    {
        n: "01",
        title: "Set the context",
        body: "Select your business type, sector and current stage.",
    },
    {
        n: "02",
        title: "Answer the scan",
        body: "Agree or disagree with 27 structural statements. You never describe or pitch the idea itself.",
    },
    {
        n: "03",
        title: "Receive the diagnostic",
        body: "Get a rule-based report with one dominant kill risk, key constraints and concrete next actions.",
    },
    {
        n: "04",
        title: "Optional private review",
        body: "Request a private idea & strategy review, a paid session that adds the context the scan leaves out.",
    },
];

const PRINCIPLES = [
    {
        icon: EyeSlash,
        title: "Idea-blind by design",
        body: "BoM never asks what your idea is. It scores 27 structural statements about revenue, adoption, control, execution and focus, so the verdict cannot be swayed by how exciting the idea sounds.",
    },
    {
        icon: Scales,
        title: "Structural, not motivational",
        body: "BoM does not judge whether your idea is original or exciting. It checks whether your assumptions reinforce each other or contradict each other.",
    },
    {
        icon: ShieldCheck,
        title: "Deterministic, not AI",
        body: "Every result is produced by predefined rules, score mappings and copy libraries. The same answers always produce the same verdict.",
    },
    {
        icon: Target,
        title: "Prioritized kill risks",
        body: "You receive one dominant Primary Kill Risk, plus a Secondary Kill Risk only when a second, structurally distinct threat is material. Never a vague list of failure modes.",
    },
    {
        icon: SealCheck,
        title: "Commercially serious",
        body: "Five domains: economics, adoption, control, execution, strategic coherence. Each with explicit thresholds and red-flag triggers.",
    },
];

export default function Landing() {
    useEffect(() => {
        if (sessionStorage.getItem("biks_visit_tracked")) return;
        sessionStorage.setItem("biks_visit_tracked", "1");
        trackEvent("visit");
    }, []);
    return (
        <main data-testid="landing-page" className="bg-biks-bg text-white">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative overflow-hidden border-b border-biks-line/50"
            >
                <div aria-hidden className="bg-grid absolute inset-0 opacity-60" />
                <div
                    aria-hidden
                    className="absolute -left-32 top-20 h-[420px] w-[420px] rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0) 70%)",
                    }}
                />
                <div
                    aria-hidden
                    className="absolute -right-40 bottom-0 h-[520px] w-[520px] rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(30,58,138,0.35) 0%, rgba(30,58,138,0) 70%)",
                    }}
                />
                <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-24 lg:px-10 lg:pb-36 lg:pt-32">
                    <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
                        <div className="lg:col-span-7">
                            <h1
                                data-testid="hero-title"
                                className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
                            >
                                BoM: Break or Make.
                            </h1>
                            <p
                                data-testid="hero-slogan"
                                className="mt-5 text-xl font-semibold text-biks-orange sm:text-2xl"
                            >
                                Find what breaks it. Learn what makes it.
                            </p>
                            <p
                                data-testid="hero-subtitle"
                                className="mt-5 max-w-2xl text-lg leading-relaxed text-biks-text2 sm:text-xl"
                            >
                                A structural stress test for early-stage business ideas.
                            </p>
                            <p
                                data-testid="hero-body"
                                className="mt-6 max-w-2xl text-base leading-relaxed text-biks-text2"
                            >
                                BoM helps founders and early-stage entrepreneurs
                                pressure-test the commercial structure behind a
                                business idea. It checks whether your assumptions
                                around revenue, adoption, control, execution and
                                strategic focus reinforce each other, or
                                contradict each other.
                            </p>
                            <p
                                data-testid="hero-idea-blind"
                                className="mt-5 max-w-2xl border-l-2 border-biks-blue pl-4 text-base leading-relaxed text-white"
                            >
                                You will never be asked what your idea is. BoM
                                is idea-blind: it scores only your answers to 27
                                structural statements, so the verdict is objective
                                and the idea itself stays yours.
                            </p>
                            <div className="mt-10 flex flex-col gap-4">
                                <span
                                    data-testid="hero-free-badge"
                                    className="inline-flex w-fit items-center gap-2 rounded-full border border-biks-orange/40 bg-biks-orange/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-biks-orange"
                                >
                                    100% free · no signup
                                </span>
                                <div className="flex flex-wrap items-center gap-4">
                                <Link
                                    to="/scan"
                                    data-testid="hero-start-scan-button"
                                    className="group inline-flex items-center gap-3 rounded-md bg-biks-blue px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all hover:bg-blue-500 hover:translate-x-0.5"
                                >
                                    Start the free <span className="normal-case">BoM</span> scan
                                    <ArrowRight
                                        size={16}
                                        weight="bold"
                                        className="transition-transform group-hover:translate-x-1"
                                    />
                                </Link>
                                <Link
                                    to="/contact"
                                    data-testid="hero-contact-link"
                                    className="inline-flex items-center gap-3 rounded-md border border-biks-line px-7 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:border-biks-blueSoft"
                                >
                                    Request private review
                                </Link>
                                </div>
                            </div>
                            <div
                                data-testid="hero-meta"
                                className="mt-12 grid max-w-2xl grid-cols-2 overflow-hidden rounded-lg border border-biks-line/60 bg-biks-surface/40 backdrop-blur sm:grid-cols-4"
                            >
                                <div className="border-b border-r border-biks-line/60 p-5 sm:border-b-0">
                                    <div className="font-mono-data text-2xl font-semibold text-white">5</div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-biks-text3">Domains</div>
                                </div>
                                <div className="border-b border-biks-line/60 p-5 sm:border-b-0 sm:border-r">
                                    <div className="font-mono-data text-2xl font-semibold text-white">27</div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-biks-text3">Statements</div>
                                </div>
                                <div className="border-r border-biks-line/60 p-5">
                                    <div className="font-mono-data text-2xl font-semibold text-white">0</div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-biks-text3">Idea descriptions</div>
                                </div>
                                <div className="p-5">
                                    <div className="font-mono-data text-2xl font-semibold text-white">0%</div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-biks-text3">AI</div>
                                </div>
                            </div>
                        </div>

                        {/* Abstract preview — no concrete results */}
                        <div className="lg:col-span-5">
                            <div className="section-title mb-4">What you'll receive</div>
                            <div className="overflow-hidden rounded-xl border border-biks-line/70 bg-biks-surface/70 p-6 shadow-[0_30px_80px_-30px_rgba(59,130,246,0.4)] backdrop-blur">
                                <ul className="space-y-4">
                                    {[
                                        ["01", "Overall Feasibility Index", "A single adjusted number for the structural consistency of your idea, with a short 'why this score' explanation of the top drivers."],
                                        ["02", "Primary & secondary kill risk", "The dominant unresolved tension, plus a second, structurally distinct threat when material."],
                                        ["03", "Constraint map", "Up to three structural constraints, each quoting the exact statement and answer that triggered it."],
                                        ["04", "Weakest signals & contradictions", "The statements you disagreed with, were undecided on, and any two answers that cannot both be true."],
                                        ["05", "Three concrete actions", "Specific next steps with 'done when' criteria, each tied to the answer it fixes."],
                                    ].map(([n, title, body]) => (
                                        <li key={n} className="flex gap-4 border-b border-biks-line/40 pb-4 last:border-0 last:pb-0">
                                            <span className="font-mono-data shrink-0 text-xs text-biks-blueSoft">
                                                {n}
                                            </span>
                                            <div>
                                                <div className="text-sm font-semibold text-white">
                                                    {title}
                                                </div>
                                                <div className="mt-1 text-xs text-biks-text2">
                                                    {body}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 rounded-md border border-biks-line/60 bg-biks-bg/60 p-3 text-[11px] uppercase tracking-[0.2em] text-biks-text3">
                                    Deterministic · No AI · Idea-blind · Same answers always produce the same verdict
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section data-testid="how-it-works-section" className="border-b border-biks-line/50">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                    <div className="grid gap-10 lg:grid-cols-12">
                        <div className="lg:col-span-4">
                            <div className="section-title mb-6">How it works</div>
                            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Four steps. No login. No interpretation games.
                            </h2>
                            <p className="mt-6 max-w-md text-base text-biks-text2">
                                BoM is fully deterministic. The same answers
                                always produce the same verdict. Context
                                (stage, industry and business type)
                                sharpens both the wording and the strictness
                                of the score.
                            </p>
                        </div>
                        <div className="lg:col-span-8">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {STEPS.map((s, i) => (
                                    <div
                                        key={s.n}
                                        data-testid={`how-step-${i + 1}`}
                                        className="rounded-lg border border-biks-line/60 bg-biks-surface/40 p-7 transition-colors hover:border-biks-blue/60"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono-data text-sm text-biks-blue">{s.n}</span>
                                            <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-biks-text3">
                                                Step
                                            </span>
                                        </div>
                                        <h3 className="mt-6 font-display text-xl font-semibold text-white">
                                            {s.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-biks-text2">{s.body}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section data-testid="principles-section" className="border-b border-biks-line/50">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                    <div className="section-title mb-6">What <span className="normal-case">BoM</span> is</div>
                    <h2 className="font-display max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        A diagnostic, not a pep talk.
                    </h2>
                    <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                        {PRINCIPLES.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.title}
                                    data-testid={`principle-${i + 1}`}
                                    className="rounded-lg border border-biks-line/60 bg-biks-surface/40 p-6 transition-colors hover:border-biks-blue/60"
                                >
                                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-biks-blue/15 text-biks-blueSoft">
                                        <Icon size={18} weight="bold" />
                                    </span>
                                    <h3 className="mt-6 font-display text-base font-semibold text-white">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-biks-text2">{p.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section data-testid="closing-cta-section" className="bg-biks-bg">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                    <div className="section-title mb-6">Ready when you are</div>
                    <div className="overflow-hidden rounded-xl border border-biks-line/70 bg-gradient-to-br from-biks-surface/80 to-biks-surface/20 p-10 lg:p-16">
                        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
                            <div className="lg:col-span-8">
                                <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Find out if your idea survives the scan.
                                </h2>
                                <p className="mt-6 max-w-2xl text-base text-biks-text2">
                                    Five minutes. Five domains. One verdict.
                                    No login, no email, no pitch. You never
                                    describe the idea itself.
                                </p>
                            </div>
                            <div className="lg:col-span-4 lg:text-right">
                                <Link
                                    to="/scan"
                                    data-testid="closing-start-scan-button"
                                    className="group inline-flex items-center gap-3 rounded-md bg-biks-blue px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all hover:bg-blue-500"
                                >
                                    Start the free <span className="normal-case">BoM</span> scan
                                    <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
